import * as React from "react";
import { ChevronDown, ExternalLink, LogOut, PanelLeft, PanelRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { appPath } from "@/constants/paths";
import { navMain, type AdminNavItem, type AdminNavSubItem } from "@/constants/admin-nav";
import { cn } from "@/lib/utils";
import { useLogout, useUser } from "@/features/auth/store";
import { isSuperAdmin } from "@/lib/auth-utils";
import type { UserRole } from "@/types/user";
import { useMyPermissions } from "@/features/permissions/hooks/usePermissionsQueries";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const MOF_LOGO_SRC = "/assets/logos/mof-logo.png";

const navItemBaseClass =
  "rounded-xl transition-all duration-200 w-full h-auto px-4 py-3 text-right group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!w-10 group-data-[collapsible=icon]:!h-10 group-data-[collapsible=icon]:!min-w-10 group-data-[collapsible=icon]:!p-2 group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:shadow-none";

const navItemLinkClass =
  "flex w-full items-center gap-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0";

const navItemIconClass = "size-5 shrink-0";

const navItemLabelClass =
  "min-w-0 flex-1 text-right font-medium text-base group-data-[collapsible=icon]:hidden";

const navChevronClass =
  "ms-auto size-4 shrink-0 transition-transform duration-200 group-data-[collapsible=icon]:hidden group-data-[state=open]/collapsible:rotate-180";

const navChevronSpacerClass =
  "ms-auto size-4 shrink-0 group-data-[collapsible=icon]:hidden";

const navSubItemLabelClass = "font-medium text-sm flex-1 text-right";

const NavSubLink = React.forwardRef<
  HTMLAnchorElement,
  {
    subItem: AdminNavSubItem;
    isActive: boolean;
    className?: string;
  }
>(function NavSubLink({ subItem, isActive, className, ...props }, ref) {
  const content = (
    <>
      {subItem.icon && (
        <subItem.icon
          className={cn(
            "size-4 shrink-0 transition-colors",
            isActive
              ? "text-primary-foreground"
              : "text-muted-foreground group-hover/sub:text-primary",
          )}
        />
      )}
      <span
        className={cn(
          navSubItemLabelClass,
          isActive && "text-primary-foreground",
        )}
      >
        {subItem.title}
      </span>
      {subItem.external && (
        <ExternalLink
          className={cn(
            "ms-auto size-3 shrink-0",
            isActive ? "text-primary-foreground/80" : "opacity-60",
          )}
        />
      )}
    </>
  );

  const mergedClassName = cn("flex w-full items-center gap-3", className);

  if (subItem.external) {
    return (
      <a
        ref={ref}
        href={subItem.url}
        target="_blank"
        rel="noopener noreferrer"
        className={mergedClassName}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <Link ref={ref} to={subItem.url} className={mergedClassName} {...props}>
      {content}
    </Link>
  );
});

function hasNavItems(
  item: AdminNavItem,
): item is AdminNavItem & { items: AdminNavSubItem[] } {
  return Array.isArray(item.items) && item.items.length > 0;
}

function NavCollapsibleGroup({
  item,
  isActivePath,
  parentActiveClass,
  subActiveClass,
}: {
  item: AdminNavItem & { items: AdminNavSubItem[] };
  isActivePath: (href: string) => boolean;
  parentActiveClass: (active: boolean) => string;
  subActiveClass: (active: boolean) => string;
}) {
  const hasActiveChild = item.items.some((sub) => isActivePath(sub.url));
  const [open, setOpen] = React.useState(hasActiveChild);

  React.useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            className={cn(
              navItemBaseClass,
              "justify-between group/btn",
              parentActiveClass(hasActiveChild),
            )}
          >
            <div className={navItemLinkClass}>
              {item.icon && (
                <item.icon
                  className={cn(
                    navItemIconClass,
                    "transition-colors",
                    hasActiveChild
                      ? "text-primary"
                      : "text-muted-foreground group-hover/btn:text-primary",
                  )}
                />
              )}
              <span className={navItemLabelClass}>{item.title}</span>
            </div>
            <ChevronDown className={navChevronClass} />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className="mt-1 gap-1 border-none p-0 pe-0 ps-3 ms-0">
            {item.items.map((subItem) => {
              const active = isActivePath(subItem.url);
              return (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={active}
                    className={cn(
                      "h-auto w-full justify-start rounded-lg px-3 py-2.5 text-right transition-all duration-200 group/sub",
                      "hover:bg-primary/5 hover:text-primary",
                      "data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:shadow-md",
                      subActiveClass(active),
                    )}
                  >
                    <NavSubLink subItem={subItem} isActive={active} />
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function AppSidebar({
  className,
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const user = useUser();
  const logout = useLogout();
  const { data: myPermissions } = useMyPermissions();
  const permissionSet = new Set(myPermissions?.permissions ?? []);

  const canSeeNavItem = (roles?: UserRole[], permission?: string) => {
    // A declared permission is the rule. `roles` only decides items that declare none — otherwise
    // a role in the list sees the entry however the permissions panel is set.
    if (permission) return isSuperAdmin(user) || permissionSet.has(permission);
    if (!roles || roles.length === 0) return true;
    if (!user?.role) return false;
    return roles.includes(user.role);
  };

  const visibleNav = navMain
    .map((item) => {
      if (item.items) {
        const items = item.items.filter((sub) =>
          canSeeNavItem(sub.roles, sub.permission),
        );
        if (items.length === 0) return null;
        return { ...item, items };
      }
      return canSeeNavItem(item.roles, item.permission) ? item : null;
    })
    .filter(Boolean) as typeof navMain;

  const navUrls = React.useMemo(
    () =>
      visibleNav.flatMap((item) =>
        item.items ? item.items.map((sub) => sub.url) : [item.url],
      ),
    [visibleNav],
  );

  const activeNavUrl = React.useMemo(() => {
    const { pathname, search } = location;
    const adminRoot = appPath();

    const pathMatches = navUrls.filter((url) => {
      if (url === "#") return false;
      const [urlPath] = url.split("?");
      if (urlPath === adminRoot) return pathname === adminRoot;
      return pathname === urlPath || pathname.startsWith(`${urlPath}/`);
    });

    if (pathMatches.length === 0) return null;

    const searchMatches = pathMatches.filter((url) => {
      const [, urlSearch = ""] = url.split("?");
      return `?${urlSearch}` === search || (urlSearch === "" && search === "");
    });

    const candidates = searchMatches.length > 0 ? searchMatches : pathMatches;

    return candidates.reduce((best, url) =>
      url.length > best.length ? url : best,
    );
  }, [location, navUrls]);

  const isActivePath = (href: string) => activeNavUrl === href;

  const parentActiveClass = (active: boolean) =>
    active
      ? "bg-primary/5 text-primary"
      : "text-foreground hover:bg-primary/5 hover:text-primary";

  const subActiveClass = (active: boolean) =>
    active
      ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
      : "text-foreground";

  return (
    <Sidebar
      collapsible="icon"
      side="right"
      role="navigation"
      aria-label="التنقل الرئيسي"
      className={cn(
        "**:data-[slot=sidebar-inner]:bg-card",
        "**:data-[slot=sidebar-inner]:text-foreground",
        "**:data-[slot=sidebar-inner]:border-border",
        className,
      )}
      {...props}
    >
      <SidebarHeader className="border-b border-border group-data-[collapsible=icon]:p-0">
        <div className="flex items-center justify-between gap-2 p-4 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-3">
          <img
            src={MOF_LOGO_SRC}
            alt="وزارة المالية"
            className="h-18 w-auto shrink-0 object-contain group-data-[collapsible=icon]:hidden"
          />
          <div className="hidden flex-col items-center gap-0.5 text-center group-data-[collapsible=icon]:flex">
            <span className="text-[10px] font-bold leading-tight text-foreground">
              وزارة
            </span>
            <span className="text-[10px] font-bold leading-tight text-foreground">
              المالية
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            title={state === "collapsed" ? "توسيع" : "طي"}
            className="size-9 shrink-0 rounded-lg text-muted-foreground shadow-none ring-0 hover:bg-primary/5 hover:text-primary focus-visible:ring-0 group-data-[collapsible=icon]:size-8"
          >
            {state === "collapsed" ? (
              <PanelRight className="size-4" />
            ) : (
              <PanelLeft className="size-5" />
            )}
          </Button>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="p-0 px-4 py-4 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:py-2">
          <SidebarMenu className="gap-1 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-1">
            {visibleNav.map((item) => (
              <React.Fragment key={item.title}>
                {hasNavItems(item) ? (
                  <NavCollapsibleGroup
                    item={item}
                    isActivePath={isActivePath}
                    parentActiveClass={parentActiveClass}
                    subActiveClass={subActiveClass}
                  />
                ) : (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActivePath(item.url)}
                      tooltip={item.title}
                      className={cn(
                        navItemBaseClass,
                        "group/single",
                        isActivePath(item.url)
                          ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
                          : "text-foreground hover:bg-primary/5 hover:text-primary",
                      )}
                    >
                      <Link to={item.url} className={navItemLinkClass}>
                        {item.icon && (
                          <item.icon
                            className={cn(
                              navItemIconClass,
                              "transition-colors",
                              isActivePath(item.url)
                                ? "text-primary-foreground"
                                : "text-muted-foreground group-hover/single:text-primary",
                            )}
                          />
                        )}
                        <span className={navItemLabelClass}>{item.title}</span>
                        <span className={navChevronSpacerClass} aria-hidden />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </React.Fragment>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="mt-auto border-t border-border px-4 py-3 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-2">
        <SidebarMenu className="group-data-[collapsible=icon]:items-center">
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="تسجيل الخروج"
              onClick={() => void logout()}
              className={cn(
                navItemBaseClass,
                "text-destructive hover:bg-destructive/10 hover:text-destructive",
              )}
            >
              <div className={navItemLinkClass}>
                <LogOut className={cn(navItemIconClass, "text-destructive")} />
                <span className={navItemLabelClass}>تسجيل الخروج</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
